"use client";

import { ChangeEvent, useState } from "react";
import {
  BriefAttachment,
  generateUUID,
  MAX_BRIEF_ATTACHMENTS,
  resolveBriefAttachmentContentType,
  toast,
} from "@drpcrd/utils";
import type { BriefAttachmentUploadTarget } from "../../brief-attachments/storage";

/**
 * Uploads one attachment and resolves with the key it landed under. The host
 * app's uploads route issues the key and a signed URL; the file goes there in
 * one PUT.
 */
const uploadFile = async (file: File): Promise<string> => {
  const contentType = resolveBriefAttachmentContentType(file);
  if (!contentType) {
    throw new Error("Unsupported file type");
  }

  const targetRes = await fetch("/api/brief-attachments/uploads", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ fileType: contentType }),
  });
  if (!targetRes.ok) {
    throw new Error("Failed to start the upload");
  }
  const target: BriefAttachmentUploadTarget = await targetRes.json();

  const res = await fetch(target.url, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": contentType },
  });
  if (!res.ok) {
    throw new Error("Failed to upload the file");
  }
  return target.key;
};

type PendingUpload = { id: string; name: string };

type Options = {
  /** How many files the brief already has, so the cap counts them too. */
  attachedCount: number;
  onUploaded: (attachment: BriefAttachment) => void;
};

/**
 * Uploads the files picked in a file input, several at once, and hands each
 * one back as it lands. Enforces the brief's file cap across what is already
 * attached and what is still in flight, and reports a failed file with a
 * toast without stopping the others.
 */
export const useBriefAttachmentUploads = ({
  attachedCount,
  onUploaded,
}: Options) => {
  const [pending, setPending] = useState<PendingUpload[]>([]);

  const onFilesSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? []);
    // Clear the input so picking the same file again still fires a change.
    event.target.value = "";

    const files = picked.filter(
      (file) => resolveBriefAttachmentContentType(file) !== null,
    );
    if (files.length < picked.length) {
      toast.error("That file type can’t be attached", {
        description: "Use PDF, JPG, PNG, GIF, DOC/DOCX, XLS/XLSX, or CSV",
      });
    }
    if (files.length === 0) {
      return;
    }

    const room = Math.max(
      MAX_BRIEF_ATTACHMENTS - attachedCount - pending.length,
      0,
    );
    if (files.length > room) {
      toast.error(`You can attach up to ${MAX_BRIEF_ATTACHMENTS} files`, {
        description:
          room > 0
            ? `Only the first ${room} of your selection will be added`
            : "Remove one to add another",
      });
    }

    const uploads = files
      .slice(0, room)
      .map((file) => ({ id: generateUUID(), name: file.name, file }));
    setPending((previous) => [
      ...previous,
      ...uploads.map(({ id, name }) => ({ id, name })),
    ]);

    await Promise.all(
      uploads.map(async ({ id, name, file }) => {
        try {
          const key = await uploadFile(file);
          onUploaded({ id: key, name });
        } catch {
          toast.error(`Couldn’t upload ${name}`, {
            description: "Please try again",
          });
        } finally {
          setPending((previous) =>
            previous.filter((upload) => upload.id !== id),
          );
        }
      }),
    );
  };

  return { pending, onFilesSelected };
};
